import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import {
  Users,
  Briefcase,
  Flag,
  Inbox,
  FileText,
  Image,
  ArrowLeft,
} from "lucide-react"
import { useAuth } from "../lib/auth-context"
import { getContainer } from "../lib/container"
import { Card } from "../components/ui"

const PROBE_SIZE = 20

interface NavCardDef {
  to: string
  title: string
  description: string
  icon: typeof Users
  unitLabel: string
  probe?: () => Promise<{ count: number; hasMore: boolean }>
}

function useSignal(probe?: () => Promise<{ count: number; hasMore: boolean }>) {
  const [signal, setSignal] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!probe) return
    probe()
      .then(({ count, hasMore }) => {
        if (!cancelled) setSignal(hasMore ? `+${count}` : `${count}`)
      })
      .catch(() => {
        if (!cancelled) setSignal(null)
      })
    return () => {
      cancelled = true
    }
  }, [probe])

  return signal
}

function NavCard({ def }: { def: NavCardDef }) {
  const signal = useSignal(def.probe)
  const Icon = def.icon

  return (
    <Link to={def.to}>
      <Card className="flex h-full flex-col gap-md p-lg transition-shadow hover:shadow-modal">
        <div className="flex items-start justify-between">
          <span className="flex size-11 items-center justify-center rounded-box bg-accent/10 text-accent">
            <Icon className="size-5" />
          </span>
          {signal && (
            <span className="rounded-badge bg-surface-muted px-sm py-2xs text-caption font-semibold text-text-secondary">
              {signal} {def.unitLabel}
            </span>
          )}
        </div>
        <div className="flex-1">
          <h2 className="text-subtitle font-bold text-text-primary">{def.title}</h2>
          <p className="mt-2xs text-body-sm text-text-secondary">{def.description}</p>
        </div>
        <span className="inline-flex items-center gap-2xs text-label font-semibold text-accent">
          فتح
          <ArrowLeft className="size-4 rtl:rotate-180" />
        </span>
      </Card>
    </Link>
  )
}

export function DashboardPage() {
  const { user } = useAuth()

  const sections: NavCardDef[] = [
    {
      to: "/users",
      title: "المستخدمون",
      description: "إدارة المستخدمين والصلاحيات وتعطيل الحسابات",
      icon: Users,
      unitLabel: "مستخدم",
      probe: async () => {
        const page = await getContainer().userProfileRepository.fetchPage(PROBE_SIZE)
        return { count: page.items.length, hasMore: !!page.nextCursor }
      },
    },
    {
      to: "/services",
      title: "الخدمات",
      description: "إدارة ومراجعة الخدمات المعلنة وحذف المخالف منها",
      icon: Briefcase,
      unitLabel: "خدمة",
      probe: async () => {
        const page = await getContainer().serviceRepository.fetchPage(PROBE_SIZE)
        return { count: page.items.length, hasMore: !!page.nextCursor }
      },
    },
    {
      to: "/reports",
      title: "التقارير",
      description: "مراجعة البلاغات المقدمة على الخدمات والتعليقات والتقييمات",
      icon: Flag,
      unitLabel: "بلاغ",
      probe: async () => {
        const page = await getContainer().reportsRepository.fetchPage(PROBE_SIZE)
        return { count: page.items.length, hasMore: !!page.nextCursor }
      },
    },
    {
      to: "/support",
      title: "الرسائل",
      description: "الاطلاع على رسائل التواصل الواردة من المستخدمين والرد عليها",
      icon: Inbox,
      unitLabel: "رسالة",
      probe: async () => {
        const page = await getContainer().contactRepository.fetchPage(PROBE_SIZE)
        return { count: page.items.length, hasMore: !!page.nextCursor }
      },
    },
    {
      to: "/content",
      title: "المحتوى القانوني",
      description: "تعديل سياسة الخصوصية وشروط الاستخدام المعروضة للمستخدمين",
      icon: FileText,
      unitLabel: "",
    },
    {
      to: "/ad-banner",
      title: "شريط الإعلانات",
      description: "التحكم بشريط الإعلان الترويجي الظاهر داخل التطبيق",
      icon: Image,
      unitLabel: "",
    },
  ]

  return (
    <div className="flex flex-col gap-2xl">
      <div>
        <h1 className="text-display font-bold text-text-primary">لوحة التحكم</h1>
        <p className="mt-2xs text-body-sm text-text-secondary">مرحباً {user?.email} — أنت مسؤول.</p>
      </div>

      <div className="grid gap-lg sm:grid-cols-2 lg:grid-cols-3">
        {sections.map((section) => (
          <NavCard key={section.to} def={section} />
        ))}
      </div>
    </div>
  )
}
